// Semantic chart spec → Highcharts options. The backend never sends raw
// Highcharts config — only this fixed vocabulary — so every option here is
// authored client-side (no config injection surface).
import type { DashboardChart } from '../../../types';

const FONT = 'Inter, system-ui, sans-serif';
const PALETTE = [
  '#6366f1',
  '#22c55e',
  '#f59e0b',
  '#0ea5e9',
  '#a855f7',
  '#64748b',
];

const base = {
  // Solid white, NOT 'transparent': the exporting menu's "View in full
  // screen" paints the browser's fullscreen backdrop behind the chart —
  // which is black — so a transparent chart becomes invisible. The card
  // the chart sits in is white anyway, so inline rendering is unchanged
  // (and exported PNGs stop coming out transparent too).
  chart: { style: { fontFamily: FONT }, backgroundColor: '#ffffff' },
  title: { text: null },
  credits: { enabled: false },
  legend: { enabled: false },
  colors: PALETTE,
};

export const dashboardChartOptions = (
  spec: DashboardChart
): Record<string, unknown> | null => {
  switch (spec.chartType) {
    case 'bar':
    case 'column':
      return {
        ...base,
        chart: { ...base.chart, type: spec.chartType, height: 220 },
        xAxis: { categories: spec.categories ?? [], lineColor: '#e2e8f0' },
        yAxis: {
          title: { text: spec.unit ?? null },
          gridLineColor: '#f1f5f9',
        },
        series: (spec.series ?? []).map((s) => ({
          type: spec.chartType,
          name: s.name,
          data: s.data,
        })),
        plotOptions: {
          series: { dataLabels: { enabled: true }, colorByPoint: true },
        },
      };
    case 'stacked_bar':
      return {
        ...base,
        chart: { ...base.chart, type: 'bar', height: 220 },
        xAxis: { categories: spec.categories ?? [] },
        yAxis: { title: { text: spec.unit ?? null } },
        legend: { enabled: true },
        plotOptions: { series: { stacking: 'normal' } },
        series: (spec.series ?? []).map((s) => ({
          type: 'bar',
          name: s.name,
          data: s.data,
        })),
      };
    case 'donut':
      return {
        ...base,
        chart: { ...base.chart, type: 'pie', height: 240 },
        legend: { enabled: true },
        plotOptions: {
          pie: { innerSize: '60%', dataLabels: { enabled: true } },
        },
        series: [
          {
            type: 'pie',
            name: spec.series?.[0]?.name ?? '',
            data: (spec.categories ?? []).map((c, i) => ({
              name: c,
              y: spec.series?.[0]?.data?.[i] ?? 0,
            })),
          },
        ],
      };
    case 'range':
      return rangeOptions(spec);
    default:
      return null;
  }
};

// Percentile range chart: horizontal columnrange for the P25–P75 IQR, scatter
// markers for P50 (median) and P90, dashed columnrange overlay for the
// current band, solid overlay for the recommended band.
const rangeOptions = (spec: DashboardChart): Record<string, unknown> => {
  const ranges = spec.ranges ?? [];
  const cats = ranges.map((r) => r.label);
  const iqr = ranges.map((r) => [r.p25, r.p75]);
  const medians = ranges.map((r, i) => ({ x: i, y: r.p50 }));
  const p90s = ranges.map((r, i) => ({ x: i, y: r.p90 }));

  const series: Record<string, unknown>[] = [
    {
      type: 'columnrange',
      name: 'P25–P75',
      data: iqr,
      color: 'rgba(99,102,241,0.25)',
      borderColor: '#6366f1',
      borderWidth: 1,
      pointWidth: 26,
    },
    {
      type: 'scatter',
      name: 'Median (P50)',
      data: medians,
      color: '#312e81',
      marker: { symbol: 'diamond', radius: 6 },
    },
    {
      type: 'scatter',
      name: 'P90',
      data: p90s,
      color: '#94a3b8',
      marker: { symbol: 'circle', radius: 4 },
    },
  ];

  const currents = ranges
    .map((r, i) =>
      r.currentMax ? { x: i, low: r.currentMin ?? 0, high: r.currentMax } : null
    )
    .filter(Boolean);
  if (currents.length > 0) {
    series.push({
      type: 'columnrange',
      name: 'Current band',
      data: currents,
      color: 'transparent',
      borderColor: '#f59e0b',
      borderWidth: 2,
      dashStyle: 'Dash',
      pointWidth: 34,
    });
  }
  const recs = ranges
    .map((r, i) =>
      r.recMax ? { x: i, low: r.recMin ?? 0, high: r.recMax } : null
    )
    .filter(Boolean);
  if (recs.length > 0) {
    series.push({
      type: 'columnrange',
      name: 'Recommended',
      data: recs,
      color: 'rgba(34,197,94,0.25)',
      borderColor: '#16a34a',
      borderWidth: 2,
      pointWidth: 18,
    });
  }

  const unit = spec.unit ? `${spec.unit} ` : '';
  return {
    ...base,
    chart: { ...base.chart, inverted: true, height: 120 + ranges.length * 60 },
    legend: { enabled: true },
    xAxis: { categories: cats, lineColor: '#e2e8f0' },
    yAxis: { title: { text: spec.unit ?? null }, gridLineColor: '#f1f5f9' },
    // Named values, not raw x/y coordinates: the default scatter tooltip
    // reads "x: 0 · y: 225 000", which is meaningless to a recruiter.
    tooltip: {
      formatter(this: {
        series: { name: string };
        point: { low?: number; high?: number; y?: number };
      }) {
        const fmt = (n?: number) =>
          typeof n === 'number' ? unit + n.toLocaleString('en-US') : '';
        if (typeof this.point.high === 'number') {
          return `<b>${this.series.name}</b>: ${fmt(this.point.low)} – ${fmt(this.point.high)}`;
        }
        return `<b>${this.series.name}</b>: ${fmt(this.point.y)}`;
      },
    },
    series,
  };
};
